import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import { Menu } from '@headlessui/react';
import {
    EllipsisVerticalIcon,
    CurrencyRupeeIcon,
    ChevronDownIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    UserIcon,
    CheckIcon,
    XCircleIcon,
    XMarkIcon,
    BanknotesIcon,
    WalletIcon
} from '@heroicons/react/24/outline';
import { TbFilterPause } from "react-icons/tb";
import 'react-datepicker/dist/react-datepicker.css';
import sessionCheck from "../session/userData";
import { encryptDataHybrid } from "../enc/useEncrypt";
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { motion, AnimatePresence } from 'framer-motion';
import { Switch } from '@headlessui/react';
import DatePicker from 'react-datepicker';
import { getDeviceInfo } from '../utils/deviceInfo';

const API_URL = process.env.REACT_APP_API_URL;

// Static merchant data
const staticMerchants = [];

const TopTopupUser = ({ children }) => {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [topUserFilter, setTopUserFilter] = useState('Top 10');
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

    // Filter state
    const [filters, setFilters] = useState({
        dateRange: 'This Month',
        startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        endDate: new Date()
    });

    // Date range options
    const dateRangeOptions = ['Today', 'This Week', 'This Month', 'Last Month', 'Custom Range'];

    /// Check session & encryption
    const [access_data, setAccess_data] = useState({});

    useEffect(() => {
        if (sessionCheck() == null) {
            navigate("../login", { replace: true });
        } else {
            setAccess_data(JSON.parse(sessionCheck()));
        }
    }, []);

    useEffect(() => {
        if (access_data?.username) {
            fetchReport();
        }
    }, [access_data?.username, topUserFilter, filters]);

    useEffect(() => {
        if (filterDrawerOpen) {
            // disable scrolling
            document.body.style.overflow = "hidden";
        } else {
            // enable scrolling again
            document.body.style.overflow = "auto";
        }

        // cleanup when component unmounts
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [filterDrawerOpen]);

    // Pagination state
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10
    });

    // Load static data initially
    useEffect(() => {
        setLoading(true);
        setUsers(staticMerchants);
        setFilteredUsers(staticMerchants);
        setPagination(prev => ({
            ...prev,
            totalItems: staticMerchants.length,
            totalPages: Math.ceil(staticMerchants.length / prev.itemsPerPage)
        }));
        setLoading(false);
    }, []);

    const handleTopUserFilterChange = (value) => {
        const newFilter = `Top ${value}`;
        setTopUserFilter(newFilter);
        // Update items per page based on selection
        const itemsPerPage = parseInt(value);
        setPagination(prev => ({
            ...prev,
            itemsPerPage,
            currentPage: 1,
            totalPages: Math.ceil(filteredUsers.length / itemsPerPage)
        }));
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, currentPage: newPage }));
        }
    };

    // Calculate paginated data
    const getPaginatedData = () => {
        const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
        const endIndex = startIndex + pagination.itemsPerPage;
        return filteredUsers.slice(startIndex, endIndex);
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const fetchReport = async () => {
        setLoading(true);

        try {
            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    query: 'fetch-top-topup',
                    count: parseInt(topUserFilter.split(' ')[1]),
                    dateRange: filters.dateRange,
                    fromDate: filters.startDate.toISOString().split('T')[0],
                    toDate: filters.endDate.toISOString().split('T')[0],
                }
            };

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);

            if (!encryptedMessage) {
                toast.error("Encryption failed. Please try again.");
                setLoading(false);
                return;
            }

            const payloadToSend = {
                encrypted_key: encryptedMessage.encryptedKey,
                encrypted_data: encryptedMessage.encryptedData,
            };

            const response = await fetch(`${API_URL}user-operation.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify(payloadToSend),
            });

            const result = await response.json();

            if (result.status === true) {
                setUsers(result.data);
                setFilteredUsers(result.data);

                setPagination(prev => ({
                    ...prev,
                    currentPage: 1,
                    totalItems: result.data.length,
                    totalPages: Math.ceil(result.data.length / prev.itemsPerPage)
                }));
            } else {
                toast.error(result.message || "Failed to fetch user data.");
                setUsers(staticMerchants);
                setFilteredUsers(staticMerchants);
                setPagination(prev => ({
                    ...prev,
                    currentPage: 1,
                    totalItems: staticMerchants.length,
                    totalPages: Math.ceil(staticMerchants.length / prev.itemsPerPage)
                }));
            }
        } catch (error) {
            console.error("Fetch user error:", error);
            toast.error("An unexpected error occurred. Please try again.");
            setUsers(staticMerchants);
            setFilteredUsers(staticMerchants);
            setPagination(prev => ({
                ...prev,
                currentPage: 1,
                totalItems: staticMerchants.length,
                totalPages: Math.ceil(staticMerchants.length / prev.itemsPerPage)
            }));
        } finally {
            setLoading(false);
        }
    };

    const handleDateRangeChange = (range) => {
        const today = new Date();
        let startDate, endDate = today;

        switch (range) {
            case 'Today':
                startDate = new Date(today);
                startDate.setHours(0, 0, 0, 0);
                endDate = new Date(today);
                endDate.setHours(23, 59, 59, 999);
                break;
            case 'This Week':
                startDate = new Date(today);
                startDate.setDate(today.getDate() - today.getDay());
                startDate.setHours(0, 0, 0, 0);
                endDate = new Date(today);
                endDate.setDate(today.getDate() + (6 - today.getDay()));
                endDate.setHours(23, 59, 59, 999);
                break;
            case 'This Month':
                startDate = new Date(today.getFullYear(), today.getMonth(), 1);
                endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                endDate.setHours(23, 59, 59, 999);
                break;
            case 'Last Month':
                startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                endDate = new Date(today.getFullYear(), today.getMonth(), 0);
                endDate.setHours(23, 59, 59, 999);
                break;
            default:
                startDate = filters.startDate;
                endDate = filters.endDate;
        }

        setFilters(prev => ({
            ...prev,
            dateRange: range,
            startDate,
            endDate
        }));
    };

    const applyFilters = () => {
        setFilterDrawerOpen(false);
        fetchReport(); // Refetch data with new filters
    };

    const resetFilters = () => {
        setFilters({
            dateRange: 'This Month',
            startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
            endDate: new Date()
        });
        setTopUserFilter('Top 10');
        setPagination(prev => ({
            ...prev,
            itemsPerPage: 10,
            currentPage: 1
        }));
    };

    return (
        <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-white">
            <div className="flex flex-col flex-1">
                <Header />

                {/* Filter Drawer */}
                <AnimatePresence>
                    {filterDrawerOpen && (
                        <motion.div
                            initial={{ x: '-100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '-100%' }}
                            transition={{ type: 'tween' }}
                            className="fixed inset-y-0 left-0 z-50 w-80 bg-white dark:bg-gray-800 shadow-lg overflow-y-auto"
                        >
                            <div className="p-4">
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-lg font-medium">Filter Top</h2>
                                    <button
                                        onClick={() => setFilterDrawerOpen(false)}
                                        className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                                    >
                                        <XMarkIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    {/* Date Range */}
                                    <div className="space-y-1">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Joining Date</label>
                                        <Menu as="div" className="relative">
                                            <Menu.Button className="w-full flex justify-between items-center px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-left text-sm focus:outline-none">
                                                <span>{filters.dateRange}</span>
                                                <ChevronDownIcon className="h-4 w-4" />
                                            </Menu.Button>
                                            <Menu.Items className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none">
                                                {dateRangeOptions.map((option) => (
                                                    <Menu.Item key={option}>
                                                        {({ active }) => (
                                                            <button
                                                                onClick={() => handleDateRangeChange(option)}
                                                                className={`${active ? 'bg-gray-100 dark:bg-gray-600' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                                                            >
                                                                {option}
                                                            </button>
                                                        )}
                                                    </Menu.Item>
                                                ))}
                                            </Menu.Items>
                                        </Menu>
                                        {filters.dateRange === 'Custom Range' && (
                                            <div className="grid grid-cols-2 gap-2 mt-2">
                                                <div>
                                                    <DatePicker
                                                        selected={filters.startDate}
                                                        onChange={(date) => handleFilterChange('startDate', date)}
                                                        selectsStart
                                                        startDate={filters.startDate}
                                                        endDate={filters.endDate}
                                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-700"
                                                    />
                                                </div>
                                                <div>
                                                    <DatePicker
                                                        selected={filters.endDate}
                                                        onChange={(date) => handleFilterChange('endDate', date)}
                                                        selectsEnd
                                                        startDate={filters.startDate}
                                                        endDate={filters.endDate}
                                                        minDate={filters.startDate}
                                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-700"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="space-y-1">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">No of user</label>
                                        <Menu as="div" className="relative">
                                            <Menu.Button className="w-full flex justify-between items-center px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-left text-sm focus:outline-none">
                                                <span>{topUserFilter}</span>
                                                <ChevronDownIcon className="h-4 w-4" />
                                            </Menu.Button>
                                            <Menu.Items className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none">
                                                {['10', '20', '50', '100', '150', '200'].map((option) => (
                                                    <Menu.Item key={option}>
                                                        {({ active }) => (
                                                            <button
                                                                onClick={() => handleTopUserFilterChange(option)}
                                                                className={`${active ? 'bg-gray-100 dark:bg-gray-600' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                                                            >
                                                                Top {option}
                                                            </button>
                                                        )}
                                                    </Menu.Item>
                                                ))}
                                            </Menu.Items>
                                        </Menu>
                                    </div>
                                </div>

                                <div className="mt-6 flex space-x-3">
                                    <button
                                        onClick={resetFilters}
                                        className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                                    >
                                        Reset
                                    </button>
                                    <button
                                        onClick={applyFilters}
                                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                    >
                                        Apply Filters
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Overlay when filter drawer is open */}
                {filterDrawerOpen && (
                    <div
                        className="fixed inset-0 bg-black bg-opacity-50 z-40"
                        onClick={() => setFilterDrawerOpen(false)}
                    />
                )}

                <main className="flex-1 p-6 overflow-auto">
                    <div className="max-w-7xl mx-auto">
                        <div className="flex items-center justify-between mb-6">
                            <button
                                onClick={() => navigate(-1)}
                                className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-600"
                            >
                                <ChevronLeftIcon className="h-5 w-5 mr-1" />
                                Back
                            </button>
                            <h2 className="text-xl font-semibold">
                                Top User Topup
                            </h2>
                            <button
                                onClick={() => setFilterDrawerOpen(true)}
                                className="p-2 rounded-full hover:bg-gray-300 bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center"
                            >
                                <TbFilterPause className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                            </button>
                        </div>
                        {children || (
                            <>
                                {/* User Cards */}
                                <div className="space-y-4">
                                    {loading && (
                                        <div className="flex justify-center items-center py-12">
                                            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
                                        </div>
                                    )}

                                    {!loading && getPaginatedData().length > 0 ? (
                                        <>
                                            {getPaginatedData().map((user) => (
                                                <div
                                                    key={user.user_id}
                                                    className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
                                                >
                                                    <div className="p-4 sm:p-6">
                                                        <div className="flex items-start justify-between">
                                                            <div>
                                                                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                                                    {user.name}
                                                                </h3>
                                                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                                                    ID: {user.user_id}
                                                                </p>
                                                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                                                    Mobile: {user.mobile}
                                                                </p>
                                                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                                                    Email: {user.email}
                                                                </p>
                                                            </div>

                                                            <div className="flex items-center gap-2">
                                                                {/* Status Badge */}
                                                                <div
                                                                    className={`px-2 py-1 rounded-full text-xs font-medium ${user.status === "1"
                                                                            ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                                                            : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                                                                        }`}
                                                                >
                                                                    {user.status === "1" ? "Active" : "Inactive"}
                                                                </div>

                                                                {/* Actions as Icon Buttons */}
                                                                <button
                                                                    onClick={() =>
                                                                        window.open(`/profile?userId=${user.user_id}`, "_blank")
                                                                    }
                                                                    className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                                                                    title="View Profile"
                                                                >
                                                                    <UserIcon className="h-5 w-5 text-gray-500 dark:text-gray-300" />
                                                                </button>

                                                                <button
                                                                    onClick={() =>
                                                                        window.open(`/order-info?userId=${user.user_id}`, "_blank")
                                                                    }
                                                                    className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                                                                    title="Transactions"
                                                                >
                                                                    <BanknotesIcon className="h-5 w-5 text-gray-500 dark:text-gray-300" />
                                                                </button>

                                                                <button
                                                                    onClick={() =>
                                                                        window.open(`/ledger?userId=${user.user_id}`, "_blank")
                                                                    }
                                                                    className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                                                                    title="Wallet History"
                                                                >
                                                                    <WalletIcon className="h-5 w-5 text-gray-500 dark:text-gray-300" />
                                                                </button>
                                                            </div>
                                                        </div>

                                                        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                                                            {/* Total Transactions */}
                                                            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                                                                <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                                                                    Total Count
                                                                </p>
                                                                <p className="text-lg font-bold text-blue-800 dark:text-blue-200">
                                                                    {user.total_count}
                                                                </p>
                                                            </div>

                                                            {/* Recharge Count */}
                                                            <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                                                                <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                                                                    Total Amount
                                                                </p>
                                                                <p className="text-lg font-bold text-green-800 dark:text-green-200">
                                                                    {user.total_topup}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </>
                                    ) : (
                                        !loading && (
                                            <div className="text-center py-12">
                                                <p className="text-gray-500 dark:text-gray-400">No users found matching your criteria</p>
                                            </div>
                                        )
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </main>
            </div>
            <ToastContainer />
        </div>
    );
};

export default TopTopupUser;